import { Helmet } from 'react-helmet-async'
import { FadeIn } from '@components/animations/FadeIn/FadeIn'
import { Award, Target, Users, Lightbulb, Building2, Globe2, Shield, Sparkles } from 'lucide-react'
import { motion } from 'framer-motion'

const values = [
  {
    icon: Lightbulb,
    title: 'Innovation',
    description: 'Continuously evolving with cutting-edge technology solutions for government and institutional needs.',
    color: '#6366f1',
  },
  {
    icon: Target,
    title: 'Excellence',
    description: 'Delivering quality that exceeds expectations with timely execution and compliance.',
    color: '#8b5cf6',
  },
  {
    icon: Users,
    title: 'Collaboration',
    description: 'Working together with government departments as trusted partners for sustainable growth.',
    color: '#ec4899',
  },
  {
    icon: Shield,
    title: 'Integrity',
    description: 'Transparent, ethical, and accountable in all operations and project execution.',
    color: '#06b6d4',
  },
]

const stats = [
  { icon: Building2, value: '50+', label: 'Government Clients' },
  { icon: Users, value: '50+', label: 'Institutions Served' },
  { icon: Award, value: '15+', label: 'Years Excellence' },
  { icon: Globe2, value: '100+', label: 'Projects Delivered' },
]

const sectors = [
  { name: 'Education', description: 'Smart Classrooms, E-Learning, Campus Management' },
  { name: 'Healthcare', description: 'Hospital Infrastructure, Medical Equipment, Health IT' },
  { name: 'Infrastructure', description: 'Smart Campus, Sports Facilities, Educational Infrastructure' },
  { name: 'Digital Transformation', description: 'Government Portals, Enterprise Software, Cloud' },
]

const About = () => {
  return (
    <>
      <Helmet>
        <title>About Us | Eonixa Dynamics - Government Technology Solutions</title>
        <meta name="description" content="Eonixa Dynamics - A dynamic organization specializing in comprehensive solutions for government and institutional projects since 2010. Innovation, sustainability, and excellence." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[60vh] overflow-hidden pt-24">
        <div className="container-enterprise relative z-10 py-20">
          <FadeIn>
            <div className="text-center max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full mb-6"
                style={{
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
                  border: '1px solid rgba(99, 102, 241, 0.25)',
                  boxShadow: '0 0 30px rgba(99, 102, 241, 0.1)',
                }}
              >
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-sm text-slate-300">Who We Are</span>
              </motion.div>
              <h1 className="mb-6 text-4xl font-bold text-white sm:text-5xl lg:text-6xl">
                About <span className="gradient-text">Eonixa Dynamics</span>
              </h1>
              <p className="text-lg text-slate-400 max-w-3xl mx-auto leading-relaxed">
                A dynamic and rapidly growing organization specializing in comprehensive solutions for government and institutional projects.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* About Content */}
      <section className="py-24 relative overflow-hidden">
        <div className="container-enterprise relative z-10">
          <div className="grid gap-12 lg:grid-cols-2 items-start">
            {/* Left Side - Our Story */}
            <FadeIn delay={0.1}>
              <div className="rounded-3xl p-8"
                style={{
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.05) 100%)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  backdropFilter: 'blur(12px)',
                }}
              >
                <h2 className="mb-6 text-2xl font-bold text-white">Our Story</h2>
                <div className="space-y-4 text-slate-300 leading-relaxed text-justify">
                  <p>
                    <strong className="text-white">Eonixa Dynamics</strong> is a dynamic and rapidly growing organization
                    specializing in providing comprehensive solutions for government and institutional projects.
                    <strong className="text-primary"> Established in 2010</strong>, the company has built a strong reputation
                    for delivering high-quality products and services across Maharashtra.
                  </p>
                  <p>
                    With a strong focus on <strong className="text-white">innovation, sustainability, and efficiency</strong>,
                    Eonixa Dynamics actively participates in government tenders and executes projects across multiple sectors
                    including education, healthcare, infrastructure, and digital transformation.
                  </p>
                  <p>
                    <strong className="text-white">Headquartered in Pune, Maharashtra</strong>, the company serves both
                    government and private sector clients, ensuring timely delivery, compliance, and excellence in execution.
                  </p>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-4 mt-8">
                  {stats.map((stat) => {
                    const Icon = stat.icon
                    return (
                      <div key={stat.label}
                        className="p-4 rounded-xl"
                        style={{
                          background: 'rgba(255, 255, 255, 0.03)',
                          border: '1px solid rgba(255, 255, 255, 0.06)',
                        }}
                      >
                        <Icon className="w-6 h-6 text-primary mb-2" />
                        <p className="text-2xl font-bold text-white">{stat.value}</p>
                        <p className="text-sm text-slate-400">{stat.label}</p>
                      </div>
                    )
                  })}
                </div>
              </div>
            </FadeIn>

            {/* Right Side - Why Eonixa & Sectors */}
            <FadeIn delay={0.2}>
              <div className="space-y-6">
                {/* Why Eonixa */}
                <div className="rounded-3xl p-8"
                  style={{
                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
                    border: '1px solid rgba(16, 185, 129, 0.2)',
                  }}
                >
                  <h2 className="mb-6 text-2xl font-bold text-white">Why Eonixa Dynamics?</h2>
                  <ul className="space-y-4">
                    {[
                      '15+ years of proven track record',
                      'ISO 9001, 14001, 27001, 45001, 50001, 20000 Certified',
                      '50+ successful project implementations',
                      'Pan-Maharashtra presence with local execution',
                      'Strong partnerships with leading OEMs',
                      'AIOTA, BIFMA, Green Pro, Green Guard Certified',
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full"
                          style={{
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          }}
                        >
                          <span className="text-white text-xs">✓</span>
                        </span>
                        <span className="text-slate-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Sectors */}
                <div className="rounded-3xl p-8"
                  style={{
                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.05) 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                  }}
                >
                  <h3 className="mb-6 text-xl font-bold text-white">Sectors We Serve</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {sectors.map((sector) => (
                      <div key={sector.name}
                        className="p-4 rounded-xl"
                        style={{
                          background: 'rgba(255, 255, 255, 0.03)',
                          border: '1px solid rgba(255, 255, 255, 0.06)',
                        }}
                      >
                        <h4 className="font-semibold text-white mb-1">{sector.name}</h4>
                        <p className="text-xs text-slate-400">{sector.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 relative overflow-hidden">
        <div className="container-enterprise relative z-10">
          <FadeIn delay={0.3}>
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full mb-6"
                style={{
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
                  border: '1px solid rgba(99, 102, 241, 0.25)',
                }}
              >
                <Award className="w-4 h-4 text-primary" />
                <span className="text-sm text-slate-300">Our Foundation</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Core Values</h2>
              <p className="text-lg text-slate-400 max-w-2xl mx-auto">
                The principles that guide our commitment to excellence
              </p>
            </div>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {values.map((value) => {
                const Icon = value.icon
                return (
                  <motion.div
                    key={value.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    whileHover={{ y: -5 }}
                    className="group rounded-2xl p-8 text-center"
                    style={{
                      background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.05) 100%)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                    }}
                  >
                    <div className="mx-auto mb-6 w-16 h-16 rounded-2xl flex items-center justify-center"
                      style={{
                        background: `${value.color}20`,
                        border: `1px solid ${value.color}40`,
                      }}
                    >
                      <Icon className="w-8 h-8" style={{ color: value.color }} />
                    </div>
                    <h3 className="mb-3 text-xl font-semibold text-white">{value.title}</h3>
                    <p className="text-sm text-slate-400 leading-relaxed">{value.description}</p>
                  </motion.div>
                )
              })}
            </div>
          </FadeIn>
        </div>
      </section>
    </>
  )
}

export default About